import type { CuttingDocument } from '../model/types';
import type { OptimizationResult } from '@pomoshnik/shared';
import { renderCuttingPdf } from './cutPdf';
import { copyShareTextToClipboard } from '@/lib/clipboardCopy';

/**
 * Block 14 «Раскрой» — нативный шаринг PDF карты раскроя (PR share-cut-pdf).
 *
 * Паттерн скопирован cutting-local из КП `calculator/quote/services/shareQuotePdf.ts`
 * (НЕ импорт из другой feature, P-026): PDF карты раскроя отправляется через Web
 * Share API (системное окно «Поделиться» с файлом + текстом) — Web Share принимает
 * `application/pdf`. CSV/Excel сюда не идут (Web Share не принимает эти типы —
 * `canShare` для них возвращает false; они скачиваются в модалке).
 *
 * Тонкости, унаследованные от КП:
 *  - `canShareFiles` обёрнут в try/catch — на iOS Safari 14 WebKit `canShare`
 *    может throw TypeError вместо return false.
 *  - `copyShareTextToClipboard` ДО `navigator.share` — WhatsApp/Telegram часто
 *    игнорят `ShareData.text` при вложении файла; текст остаётся в clipboard для
 *    ручной вставки (B-141). clipboard-хелпер — общий (`@/lib/clipboardCopy`).
 *  - AbortError / NotAllowedError = тихая отмена пользователем (закрыл sheet).
 *  - Нет Web Share / canShare=false / share-ошибка (не отмена) → fallback download.
 *
 * Клиентское (P-009): ничего не уходит на сервер.
 *
 * DI-сеймы `nav`/`download`/`buildPdf` — для тестов (defaults к реальным).
 * `renderCuttingPdf` тяжёлый (шрифт + растеризация карты), поэтому модалка
 * пред-собирает Blob ЗАРАНЕЕ и передаёт `buildPdf: async () => preBuiltBlob` —
 * чтобы первый реальный await в обработчике клика был `navigator.share` и не
 * рвалась transient-activation (popup gesture).
 */

export type CutShareResult = 'shared' | 'cancelled' | 'downloaded' | 'error';

export interface ShareCutPdfOptions {
  /** DI seam для тестов — defaults to global `navigator`. */
  nav?: Pick<Navigator, 'canShare' | 'share' | 'clipboard'>;
  /** Override download trigger — defaults to `downloadFile`. */
  download?: (file: File) => void;
  /** Override PDF builder — defaults to `renderCuttingPdf`. В модалке — пред-собранный Blob. */
  buildPdf?: (doc: CuttingDocument, result: OptimizationResult) => Promise<Blob>;
}

export function buildCutShareText(doc: CuttingDocument): string {
  const name = doc.meta.name?.trim() || 'без названия';
  return `Здравствуйте! Карта раскроя проекта «${name}» — во вложении.`;
}

interface ShareCanShareNav {
  canShare?: Navigator['canShare'];
  share?: Navigator['share'];
}

/**
 * `canShare({ files: [...] })` может throw TypeError на iOS Safari 14 WebKit
 * билдах вместо return false. Wrap в try/catch.
 */
function canShareFiles(nav: ShareCanShareNav | undefined, data: ShareData): boolean {
  try {
    return !!nav?.canShare?.(data) && typeof nav.share === 'function';
  } catch {
    return false;
  }
}

/** AbortError / NotAllowedError → пользователь закрыл native sheet — тихая отмена. */
function isCancelled(err: unknown): boolean {
  return err instanceof Error && (err.name === 'AbortError' || err.name === 'NotAllowedError');
}

/** Скачивание файла (паттерн `ResultActions.handleExportPdf`). */
function downloadFile(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function shareCuttingPdf(
  doc: CuttingDocument,
  result: OptimizationResult,
  opts: ShareCutPdfOptions = {},
): Promise<CutShareResult> {
  const nav = opts.nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);
  const download = opts.download ?? downloadFile;
  const buildPdf = opts.buildPdf ?? renderCuttingPdf;

  try {
    const blob = await buildPdf(doc, result);
    const file = new File([blob], `Карта_раскроя_${new Date().toISOString().slice(0, 10)}.pdf`, {
      type: 'application/pdf',
    });
    const text = buildCutShareText(doc);
    const title = `Карта раскроя — ${doc.meta.name?.trim() || 'без названия'}`;

    // B-141: текст в clipboard ДО share — WhatsApp/Telegram часто игнорят
    // ShareData.text при вложении файла. Non-fatal на failure.
    await copyShareTextToClipboard(text, {
      clipboard: nav?.clipboard as Pick<Clipboard, 'writeText'> | undefined,
    });

    const data: ShareData = { files: [file], title, text };
    if (canShareFiles(nav, data) && nav) {
      try {
        await nav.share!(data);
        return 'shared';
      } catch (e) {
        if (isCancelled(e)) return 'cancelled';
        download(file);
        return 'downloaded';
      }
    }

    download(file);
    return 'downloaded';
  } catch {
    return 'error';
  }
}
